import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { zoomForHotspots } from './hotspotZoom';
import { HotspotZoomAnchor, useHotspotGestures } from './useHotspotGestures';
import {
  HotspotPosition,
  HOTSPOT_INSETS,
  HOTSPOT_SCROLL_INSETS
} from './interactive3Placement';

export function useHotspotZoom(
  enabled: boolean,
  size: { width: number; height: number },
  points: HotspotPosition[],
  imageRef: RefObject<HTMLDivElement>,
  containerRef: RefObject<HTMLDivElement>,
  {
    insets = HOTSPOT_INSETS,
    canZoom = true,
    minTop = 0,
    viewportRef: suppliedViewportRef
  }: {
    insets?: typeof HOTSPOT_INSETS;
    canZoom?: boolean;
    minTop?: number;
    viewportRef?: RefObject<HTMLDivElement>;
  } = {}
) {
  const [level, setZoom] = useState(1);
  const zoom = enabled && canZoom ? level : 1;
  const defaultViewportRef = useRef<HTMLDivElement>(null);
  const viewportRef = suppliedViewportRef ?? defaultViewportRef;
  const usefulZoom =
    enabled && canZoom ? zoomForHotspots(points, size.width, size.height) : 1;
  const maxZoom = Math.max(8, Math.ceil(usefulZoom), zoom);
  const pendingCenter = useRef<{
    x: number;
    y: number;
    offsetX?: number;
    offsetY?: number;
  } | null>(null);
  const previous = useRef<{
    left: number;
    top: number;
    width: number;
    height: number;
    viewportWidth: number;
    viewportHeight: number;
  } | null>(null);

  const changeZoom = (next: number, anchor?: HotspotZoomAnchor) => {
    const image = imageRef.current?.getBoundingClientRect();
    const viewport = viewportRef.current?.getBoundingClientRect();
    const nextZoom = Math.max(1, Math.min(maxZoom, next));
    pendingCenter.current =
      nextZoom !== 1 && image && viewport
        ? {
            x:
              anchor?.x ??
              (viewport.left + viewport.width / 2 - image.left) / image.width,
            y:
              anchor?.y ??
              (viewport.top + viewport.height / 2 - image.top) / image.height,
            offsetX: anchor
              ? anchor.clientX - viewport.left
              : viewport.width / 2,
            offsetY: anchor
              ? anchor.clientY - viewport.top
              : viewport.height / 2
          }
        : { x: 0.5, y: 0.5 };
    if (nextZoom === zoom && viewportRef.current) {
      // Reset also recenters a map that was panned at its default size.
      viewportRef.current.scrollTo(
        left +
          pendingCenter.current.x * width -
          (pendingCenter.current.offsetX ??
            viewportRef.current.clientWidth / 2),
        top +
          pendingCenter.current.y * height -
          (pendingCenter.current.offsetY ??
            viewportRef.current.clientHeight / 2)
      );
      pendingCenter.current = null;
    }
    setZoom(nextZoom);
  };

  useHotspotGestures(
    enabled && canZoom,
    viewportRef,
    imageRef,
    zoom,
    maxZoom,
    usefulZoom,
    changeZoom
  );

  const width = size.width * zoom;
  const height = size.height * zoom;
  // The instruction-row layout supplies its remaining viewport explicitly.
  // Existing maps keep their containing canvas basis when scrollbars consume space.
  const measurement = suppliedViewportRef
    ? viewportRef.current
    : containerRef.current;
  const canvasWidth = Math.max(
    measurement?.clientWidth ?? window.innerWidth,
    width + HOTSPOT_SCROLL_INSETS.horizontal * 2
  );
  const canvasHeight = Math.max(
    measurement?.clientHeight ?? window.innerHeight,
    height + HOTSPOT_SCROLL_INSETS.top + HOTSPOT_SCROLL_INSETS.bottom
  );
  const left = (canvasWidth - width) / 2;
  const top =
    HOTSPOT_SCROLL_INSETS.top +
    (canvasHeight -
      HOTSPOT_SCROLL_INSETS.top -
      HOTSPOT_SCROLL_INSETS.bottom -
      height) /
      2;

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!enabled) {
      setZoom(1);
      return;
    }
    if (!canZoom) setZoom(1);
    if (!viewport) return;
    const old = previous.current;
    const center: NonNullable<typeof pendingCenter.current> =
      pendingCenter.current ??
      (old && zoom > 1
        ? {
            x:
              (viewport.scrollLeft + old.viewportWidth / 2 - old.left) /
              old.width,
            y:
              (viewport.scrollTop + old.viewportHeight / 2 - old.top) /
              old.height
          }
        : { x: 0.5, y: 0.5 });
    const initialTop = Math.max(
      minTop,
      (viewport.clientHeight - height + insets.top - insets.bottom) / 2
    );
    viewport.scrollTo?.(
      left + center.x * width - (center.offsetX ?? viewport.clientWidth / 2),
      zoom === 1
        ? top - initialTop
        : top +
            center.y * height -
            (center.offsetY ?? viewport.clientHeight / 2)
    );
    pendingCenter.current = null;
    previous.current = {
      left,
      top,
      width,
      height,
      viewportWidth: viewport.clientWidth,
      viewportHeight: viewport.clientHeight
    };
  }, [enabled, canZoom, zoom, width, height, left, top, insets, minTop]);

  return {
    zoom,
    changeZoom,
    viewportRef,
    usefulZoom,
    maxZoom,
    width,
    height,
    canvasWidth,
    canvasHeight,
    left,
    top
  };
}
